import { useEffect, useState, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Upload, Loader2, CheckCircle, FileText, Link2, Send } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Innovator-side milestone submission panel.
 * Shown inside the selected project dialog once the industry has accepted the proposal.
 * Submission number aligns with completed installments: after industry pays installment N,
 * innovator delivers submission N to unlock installment N+1.
 */
export default function InnovatorSubmissionPanel({ project, API, token, onDone }) {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(null);
  const [link, setLink] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const totalInstallments = project.num_installments || 1;
  const installmentsPaid = project.installments_paid || 0;

  const fetchSubs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/projects/${project.id}/submissions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } finally { setLoading(false); }
  }, [API, project.id, token]);

  useEffect(() => { fetchSubs(); }, [fetchSubs]);

  // How many submissions per milestone
  const perMilestone = {};
  submissions.forEach((s) => {
    perMilestone[s.submission_number] = (perMilestone[s.submission_number] || 0) + 1;
  });

  // Innovator can submit for milestone N once industry has paid installment N and it hasn't already been delivered.
  // Also allow submitting for milestone 1 as an "initial kickoff" even before further payments.
  const nextSubmissionNumber = (() => {
    // Find the smallest N in [1..totalInstallments] where perMilestone[N] === 0 and N <= installmentsPaid
    for (let n = 1; n <= totalInstallments; n++) {
      if (!perMilestone[n]) {
        if (n <= installmentsPaid) return n;
        return null; // waiting for next installment payment
      }
    }
    return null; // all done
  })();

  const submit = async () => {
    if (!file && !link.trim()) { toast.error('Attach a PDF or provide a link'); return; }
    if (file && !file.name.toLowerCase().endsWith('.pdf')) { toast.error('Only PDF files allowed'); return; }
    if (file && file.size > 25 * 1024 * 1024) { toast.error('File exceeds 25 MB'); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (file) fd.append('file', file);
      const qs = new URLSearchParams({
        submission_number: String(nextSubmissionNumber),
        link: link.trim(),
        notes: notes.trim(),
      });
      const res = await fetch(`${API}/projects/${project.id}/submission?${qs}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        toast.success(`Milestone ${nextSubmissionNumber} submission recorded`);
        setFile(null); setLink(''); setNotes('');
        await fetchSubs();
        onDone && onDone();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Submission failed');
      }
    } finally { setSubmitting(false); }
  };

  return (
    <div className="mt-6 pt-4 border-t" data-testid="innovator-submission-panel">
      <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
        <Send className="w-4 h-4 text-primary" /> Milestone Submissions
      </h3>
      <p className="text-xs text-muted-foreground mb-3">
        After each installment is released by the industry, upload your milestone deliverables here (PDF up to 25 MB and/or an external link like GitHub, Drive, Figma).
      </p>

      {/* Existing submissions */}
      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
      ) : submissions.length === 0 ? (
        <p className="text-xs text-muted-foreground italic mb-4">No submissions yet.</p>
      ) : (
        <div className="space-y-2 mb-4">
          {submissions.map((s) => (
            <div key={s.id} className="border rounded p-3 bg-muted/20" data-testid={`submission-${s.id}`}>
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <Badge className="bg-emerald-500/10 text-emerald-700"><CheckCircle className="w-3 h-3 mr-1" />Milestone {s.submission_number}</Badge>
                <span className="text-[10px] text-muted-foreground">{new Date(s.submitted_at).toLocaleString()}</span>
              </div>
              {s.notes && <p className="text-sm text-muted-foreground mb-2">{s.notes}</p>}
              <div className="flex flex-col gap-1 text-sm">
                {s.file && (
                  <a href={`${API}/projects/${project.id}/submission-file/${s.file.file_id}`}
                     target="_blank" rel="noreferrer"
                     className="text-primary hover:underline inline-flex items-center gap-1">
                    <FileText className="w-4 h-4" /> {s.file.filename} <span className="text-[10px] text-muted-foreground">({(s.file.size/(1024*1024)).toFixed(1)} MB)</span>
                  </a>
                )}
                {s.link && (
                  <a href={s.link} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 break-all">
                    <Link2 className="w-4 h-4 shrink-0" /> {s.link}
                  </a>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* New submission form */}
      {nextSubmissionNumber ? (
        <div className="rounded-md border p-3 bg-primary/5 space-y-2">
          <p className="text-sm font-medium">Submit deliverable for Milestone {nextSubmissionNumber} of {totalInstallments}</p>
          <div>
            <label className="text-xs text-muted-foreground">Deliverable file (PDF, ≤ 25 MB)</label>
            <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="submission-file-input" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">External link (GitHub, Drive, Figma, etc.)</label>
            <Input type="url" placeholder="https://..." value={link} onChange={(e) => setLink(e.target.value)} data-testid="submission-link-input" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Notes (optional)</label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Summarise what's included in this delivery…" data-testid="submission-notes-input" />
          </div>
          <Button size="sm" onClick={submit} disabled={submitting || (!file && !link.trim())} className="gap-2" data-testid="submit-milestone-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Submit Milestone {nextSubmissionNumber}
          </Button>
        </div>
      ) : (
        installmentsPaid < totalInstallments ? (
          <p className="text-xs text-amber-700">Waiting for the industry to release the next installment before the next submission is unlocked.</p>
        ) : (
          <p className="text-xs text-emerald-700 inline-flex items-center gap-1"><CheckCircle className="w-3 h-3" /> All milestones delivered.</p>
        )
      )}
    </div>
  );
}
